import React, { useState } from 'react';
import { Tab } from './tab.component';

export enum TabsPosition {
  RIGHT = 'right',
  LEFT = 'left',
  CENTER = 'center'
}

/**
 * interface TabsProps
 */
interface TabsProps {
  data: {
    tabsList: { key: string; component: JSX.Element; id?: string; badge?: number }[];
    defaultTab?: string;
  };
  style: {
    position: TabsPosition;
  };
  eventHandlers?: {
    tabChangeHandler?: (tabKey: string, id?: string) => void;
  };
  children?: React.ReactNode;
}

/**
 * functional component Tabs
 * @param data hold key and component for each tab
 */
export const Tabs: React.FC<TabsProps> = ({ data, eventHandlers, style, children }) => {
  const initialTab =
    data.defaultTab || (data.tabsList && data.tabsList[0] ? data.tabsList[0].key : '');

  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const onClickTabItem = (tab: string) => {
    setActiveTab(tab);
    const findID = data.tabsList.find((tabItem) => tabItem.key === tab);
    if (eventHandlers?.tabChangeHandler) {
      eventHandlers.tabChangeHandler(tab, findID?.id);
    }
  };

  const containerClass = `flex flex-col ${
    style.position === TabsPosition.RIGHT ? 'flex-col-reverse' : ''
  } gap-4 items-center w-full`;

  return (
    <div className={containerClass}>
      <ol
        role="tablist"
        className={`flex gap-3 items-center overflow-x-auto w-full p-1 mb-2 ${
          style.position === TabsPosition.CENTER ? 'justify-center' : 'justify-start'
        }`}
      >
        {data.tabsList.map((child) => {
          const { key, badge } = child;
          return (
            <Tab
              key={key}
              config={{ activeTab }}
              data={{ key, badge }}
              eventHandlers={{ onClickHandler: onClickTabItem }}
            />
          );
        })}
        {children}
      </ol>
      <div className="flex-1 rounded-xl  transition-all ease-in-out duration-200 w-full">
        {data.tabsList.map((child) => {
          if (child.key !== activeTab) return undefined;
          return child.component;
        })}
      </div>
    </div>
  );
};
